'use client';

import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '../lib/utils';
import { pageWindow } from '../lib/pagination';

export interface PaginatorProps {
  page: number;
  pageCount: number;
  onPageChange: (page: number) => void;
  /** Texte de contexte, ex. « 21–40 sur 491 ». */
  summary?: string;
  className?: string;
  /** Libellé accessible de la navigation (une liste par page peut en avoir plusieurs). */
  label?: string;
}

/**
 * Navigation de pages mobile-first : précédent / suivant (44 px) et
 * « Page x / y » sur mobile ; numéros de page à partir de `sm`.
 * Ne s'affiche pas s'il n'y a qu'une page.
 */
export function Paginator({
  page,
  pageCount,
  onPageChange,
  summary,
  className,
  label = 'Pagination',
}: PaginatorProps) {
  if (pageCount <= 1) return null;
  const go = (p: number) => {
    if (p >= 1 && p <= pageCount && p !== page) onPageChange(p);
  };
  const arrow =
    'border-ink-200 text-ink-700 hover:border-primary-300 inline-flex min-h-[2.75rem] min-w-[2.75rem] items-center justify-center rounded-full border bg-white transition-all active:scale-[0.98] disabled:pointer-events-none disabled:opacity-40';
  return (
    <nav
      aria-label={label}
      className={cn(
        'flex flex-col items-center gap-2 py-4 sm:flex-row sm:justify-between',
        className,
      )}
    >
      {summary ? <p className="text-ink-500 text-sm">{summary}</p> : <span />}
      <div className="flex items-center gap-2">
        <button
          type="button"
          className={arrow}
          onClick={() => go(page - 1)}
          disabled={page <= 1}
          aria-label="Page précédente"
        >
          <ChevronLeft className="h-4 w-4" aria-hidden />
        </button>
        <span className="text-ink-700 min-w-[6rem] text-center text-sm font-semibold sm:hidden">
          Page {page} / {pageCount}
        </span>
        <ol className="hidden items-center gap-1 sm:flex">
          {pageWindow(page, pageCount).map((n, i) =>
            n === null ? (
              <li key={`gap-${i}`} className="text-ink-400 px-1 text-sm" aria-hidden>
                …
              </li>
            ) : (
              <li key={n}>
                <button
                  type="button"
                  onClick={() => go(n)}
                  aria-current={n === page ? 'page' : undefined}
                  aria-label={`Page ${n}`}
                  className={cn(
                    'inline-flex min-h-[2.75rem] min-w-[2.75rem] items-center justify-center rounded-full text-sm font-semibold transition-all active:scale-[0.98]',
                    n === page ? 'bg-primary-500 text-white' : 'text-ink-700 hover:bg-ink-50',
                  )}
                >
                  {n}
                </button>
              </li>
            ),
          )}
        </ol>
        <button
          type="button"
          className={arrow}
          onClick={() => go(page + 1)}
          disabled={page >= pageCount}
          aria-label="Page suivante"
        >
          <ChevronRight className="h-4 w-4" aria-hidden />
        </button>
      </div>
    </nav>
  );
}
